import {
  Notification,
  type NotificationAISchedule,
  NotificationAIScheduleType,
  type NotificationBoard,
  type NotificationBoardRow,
  NotificationBoardRowType,
  NotificationBoardType,
  type NotificationChannel,
  type NotificationChannelMessage,
  NotificationChannelMessageType,
  NotificationChannelType,
  type NotificationChannelVoice,
  type NotificationDoc,
  NotificationDocType,
  type NotificationDrive,
  NotificationDriveType,
  type NotificationMeet,
  NotificationMeetType,
  type NotificationToDo,
  NotificationToDoType,
  type NotificationType,
  NotificationVoiceChannelType,
  type NotificationWorkspace,
  NotificationWorkspaceType,
} from '@copera/notification';
import { routes } from 'src/Router';

type ResolverMap<T extends Notification> = Record<
  T['type'],
  (notification: T) => string
>;

const channelMessageMap: ResolverMap<NotificationChannelMessage> = {
  [NotificationChannelMessageType.NEW]: ({ data }) =>
    routes.dashboard.chat.view(data.channelId),
  [NotificationChannelMessageType.UPDATE]: ({ data }) =>
    routes.dashboard.chat.view(data.channelId),
  [NotificationChannelMessageType.DELETE]: ({ data }) =>
    routes.dashboard.chat.view(data.channelId),
};

const channelMap: ResolverMap<NotificationChannel> = {
  [NotificationChannelType.NEW]: ({ data }) =>
    routes.dashboard.chat.view(data._id),
  [NotificationChannelType.UPDATE]: ({ data }) =>
    routes.dashboard.chat.view(data._id),
  [NotificationChannelType.SHARED]: ({ data }) =>
    routes.dashboard.chat.view(data._id),
  [NotificationChannelType.DELETE]: ({ data }) =>
    routes.dashboard.chat.view(data._id),
};

const channelVoiceMap: ResolverMap<NotificationChannelVoice> = {
  [NotificationVoiceChannelType.NOTES_READY]: ({ data }) =>
    routes.dashboard.docs.view(data._id),
  [NotificationVoiceChannelType.INCOMING_CALL]: ({ data }) =>
    routes.dashboard.chat.view(data._id),
};

const boardMap: ResolverMap<NotificationBoard> = {
  [NotificationBoardType.SHARED]: ({ data }) =>
    routes.dashboard.board.table({
      boardId: data._id,
      tableId: data.table._id,
    }),
  [NotificationBoardType.EDITED]: ({ data }) =>
    routes.dashboard.board.view(data._id),
};

const boardRowMap: ResolverMap<NotificationBoardRow> = {
  [NotificationBoardRowType.ASSIGNED]: ({ data }) =>
    routes.dashboard.board.rowDialog({
      row: data._id,
      board: data.boardId,
      table: data.tableId,
    }),
  [NotificationBoardRowType.NEW_COMMENT]: ({ data }) =>
    routes.dashboard.board.rowDialog({
      board: data.boardId,
      table: data.tableId,
      row: data._id,
    }),
};

const meetMap: ResolverMap<NotificationMeet> = {
  [NotificationMeetType.SHARED]: ({ data }) =>
    routes.dashboard.chat.view(data._id),
  [NotificationMeetType.REMINDER]: ({ data }) =>
    routes.dashboard.chat.view(data._id),
};

const todoMap: ResolverMap<NotificationToDo> = {
  [NotificationToDoType.COMPLETED]: ({ data }) =>
    routes.dashboard.todo.view(data._id),
  [NotificationToDoType.SHARED]: ({ data }) =>
    routes.dashboard.todo.view(data._id),
};

const docMap: ResolverMap<NotificationDoc> = {
  [NotificationDocType.NEW]: ({ data }) => routes.dashboard.docs.view(data._id),
  [NotificationDocType.UPDATE]: ({ data }) =>
    routes.dashboard.docs.view(data._id),
  [NotificationDocType.SHARED]: ({ data }) =>
    routes.dashboard.docs.view(data._id),
  [NotificationDocType.NEW_COMMENT]: ({ data }) =>
    routes.dashboard.docs.comments(data.docId || ''),
  [NotificationDocType.DELETE]: ({ data }) =>
    routes.dashboard.docs.view(data._id),
};

const workspaceMap: ResolverMap<NotificationWorkspace> = {
  [NotificationWorkspaceType.NEW_USER]: () => routes.settings.workspace.teams,
};

const driveMap: ResolverMap<NotificationDrive> = {
  [NotificationDriveType.SHARED]: () => routes.dashboard.drive.starred,
};

const aiScheduleMap: ResolverMap<NotificationAISchedule> = {
  [NotificationAIScheduleType.INCOMING_CALL]: ({ data }) =>
    routes.dashboard.chat.view(data._id),
};

const routesByType: ResolverMap<Notification> = {
  ...channelMap,
  ...channelMessageMap,
  ...channelVoiceMap,
  ...boardMap,
  ...boardRowMap,
  ...meetMap,
  ...todoMap,
  ...docMap,
  ...driveMap,
  ...aiScheduleMap,
  ...workspaceMap,
} as Record<NotificationType, (notification: Notification) => string>;

export const navigateToByType = (notification: Notification): string => {
  const route = routesByType[notification.type];
  if (!route) {
    return '';
  }
  return route(notification);
};
